"use strict";
/**
 * Location Helper
 *
 * Provides geolocation services for document signing.
 * - Mobile users: Uses location passed from Sky Lifestyle app via accessAuth
 * - Web users: Captures location via browser Geolocation API
 * Backend will verify location via server-side LetsTrade Integration API.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUserLocation = exports.requestLocationPermission = void 0;
const store_1 = require("../store");
const constants_1 = require("../constants/constants");
/**
 * Request location permission from user
 * Shows user-friendly error if permission denied
 *
 * @returns Promise<boolean> - true if permission granted, false otherwise
 */
function requestLocationPermission() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (!navigator.geolocation) {
                console.error("❌ Geolocation is not supported by this browser");
                return false;
            }
            // Request permission by attempting to get current position
            yield new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 10000,
                    maximumAge: 0,
                    enableHighAccuracy: true,
                });
            });
            return true; // Permission granted
        }
        catch (error) {
            console.error("❌ Location permission denied or unavailable:", error);
            return false; // Permission denied or error
        }
    });
}
exports.requestLocationPermission = requestLocationPermission;
/**
 * Get user's current location
 *
 * For mobile users: Returns location from accessAuth (passed from Sky Lifestyle app)
 * For web users: Fetches fresh location from browser Geolocation API
 *
 * IMPORTANT: Always fetches fresh location (maximumAge: 0) for document signing.
 * This ensures location accuracy for legal compliance.
 *
 * @returns Promise<ILocation | null>
 */
function getUserLocation() {
    return __awaiter(this, void 0, void 0, function* () {
        // MOBILE BYPASS: Use location from accessAuth if mobile user
        const { userState } = store_1.redux_store.getState();
        const { accessAuth } = userState;
        if ((accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.view_type) === constants_1.VIEW_TYPE.MOBILE && (accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.location)) {
            console.log("📱 Using location from mobile app:", accessAuth.location);
            // Explicitly pick only the API-accepted fields. The Mongo-backed accessAuth
            // subdoc can carry extra keys (e.g. Mongoose's auto-generated `_id`) which
            // Joi rejects downstream with `"location._id" is not allowed`.
            const { latitude, longitude, location_name } = accessAuth.location;
            return {
                latitude,
                longitude,
                location_name: location_name || "Mobile Location",
            };
        }
        // WEB USERS: Get location from browser
        try {
            if (!navigator.geolocation) {
                console.error("❌ Geolocation is not supported by this browser");
                return null;
            }
            // Check permission status first (if Permissions API available)
            if ("permissions" in navigator && "query" in navigator.permissions) {
                try {
                    const permissionStatus = yield navigator.permissions.query({ name: "geolocation" });
                    if (permissionStatus.state === "denied") {
                        console.error("❌ Location permission is denied");
                        return null;
                    }
                }
                catch (error) {
                    // Permissions API query failed, continue with fallback
                    console.warn("⚠️ Unable to check location permission status, attempting to get location anyway");
                }
            }
            // Always fetch fresh location (no cache) for document signing
            const position = yield new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 10000,
                    maximumAge: 0,
                    enableHighAccuracy: true, // Request high accuracy for legal compliance
                });
            });
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            // Return raw coordinates - backend will verify and get location_name via LetsTrade Integration
            return {
                latitude,
                longitude,
                location_name: `Unverified (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`, // Placeholder
            };
        }
        catch (error) {
            console.error("❌ Failed to get user location:", error);
            // Provide user-friendly error messages
            if (error.code === 1) {
                console.error("   User denied location permissions");
            }
            else if (error.code === 2) {
                console.error("   Location unavailable");
            }
            else if (error.code === 3) {
                console.error("   Location request timed out");
            }
            return null;
        }
    });
}
exports.getUserLocation = getUserLocation;
